import axios from "axios";
import { getHeaderToken } from "../utils/serviceHelper";
import { errorToast } from "@idwal/idwal-react-components";

/** Lifecycle of a config report as reported by the control-plane. */
export type ReportStatus = "generating" | "ready" | "error";

/** Response to POST /reports (202 Accepted) — the report is now being built. */
export interface GenerateReportResponse {
    status: ReportStatus;
}

/** Response to GET /reports/{inspectionId} — the poll target. `url` is the
 *  presigned PDF link, present once `status === "ready"`. */
export interface ReportStatusResponse {
    status: ReportStatus;
    url?: string;
}

/**
 * Ask the control-plane to (asynchronously) generate the Idwal Config Report for
 * an inspection. This only *triggers* the build — the control-plane fans out to
 * the pdf-creator orchestrator and returns 202; the PDF becomes available later
 * via getReportStatus. Auth is the reviewer's Cognito token (Bearer). Nothing
 * here touches ASIMS — status and URL come only from the control-plane.
 */
export const generateReport = async (
    inspectionId: string,
): Promise<GenerateReportResponse | null> => {
    // Fail loudly if the report API base is unset (config.sh writes "" when the
    // report-generation stack is absent) rather than POSTing to the same origin.
    if (!process.env.REACT_APP_REPORT_API) {
        errorToast("Report API is not configured");
        return null;
    }
    const url = process.env.REACT_APP_REPORT_API + "/reports";
    const headers = await getHeaderToken();

    try {
        const response = await axios.post<GenerateReportResponse>(url, { inspectionId }, { headers });
        // A 202 body is { status: 'generating' }; default defensively if absent.
        return response.data ?? { status: "generating" };
    } catch (error) {
        console.error("generateReport failed", error);
        errorToast(`Failed to generate report for inspection: ${inspectionId}`);
        return null;
    }
};

/**
 * Poll the control-plane for the report's current state. Returns { status, url };
 * `url` is only set once status is "ready". Auth is the reviewer's Cognito token.
 * Returns null on a transport error so the caller can keep polling to its own
 * deadline rather than treating a single blip as terminal.
 */
export const getReportStatus = async (
    inspectionId: string,
): Promise<ReportStatusResponse | null> => {
    if (!process.env.REACT_APP_REPORT_API) {
        errorToast("Report API is not configured");
        return null;
    }
    const url = process.env.REACT_APP_REPORT_API + "/reports/" + inspectionId;
    const headers = await getHeaderToken();

    try {
        const response = await axios.get<ReportStatusResponse>(url, { headers });
        return response.data;
    } catch (error) {
        console.error("getReportStatus failed", error);
        return null;
    }
};
